import React, { useEffect, useState } from 'react';
import { getSettings, updateSettings, setSkimBaseline } from '../../services/SettingsService';
import { processError } from '../../services/ServiceUtils';

// Presets de realização de lucro (skim). fraction = quanto SAI; o resto fica compondo. Devem casar
// com WITHDRAW_PROFILES no backend (loadProfiles.js). O eixo é realizar agora × deixar compondo.
const SKIM_PRESETS = [
    { key: 'SNOWBALL', emoji: '🐌', name: 'Bola de Neve', fraction: 0.25, threshold: 100, desc: 'Saca pouco — o bolo cresce rápido.' },
    { key: 'BALANCED', emoji: '⚖️', name: 'Equilibrado', fraction: 0.50, threshold: 50, desc: 'Metade sai, metade fica compondo.' },
    { key: 'HARVEST', emoji: '🌾', name: 'Colheita', fraction: 0.75, threshold: 20, desc: 'Tira a maior parte do lucro.' },
];

/**
 * Realização de lucro automática (skim). Filosofia "pague-se mas deixe o bolo crescendo": saca uma
 * fração do lucro NOVO; o resto fica compondo; o principal nunca sai. Pré-preenche os 2 knobs
 * (fração + limiar) pelos 3 presets, e mostra uma frase-resumo ao vivo. Persiste nas settings.
 */
function ProfitRealizationPanel() {
    const [percent, setPercent] = useState('');     // % que SAI (ex.: 50)
    const [threshold, setThreshold] = useState(''); // lucro novo mínimo (quote)
    const [profile, setProfile] = useState(null);   // nome do preset selecionado (ou null = personalizado)
    const [quote, setQuote] = useState('USDT');
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState(null);

    useEffect(() => {
        getSettings()
            .then(s => {
                setPercent(s.withdrawSkimFraction != null ? String(Math.round(s.withdrawSkimFraction * 100)) : '');
                setThreshold(s.withdrawSkimThreshold != null ? String(s.withdrawSkimThreshold) : '');
                setProfile(s.withdrawProfile || null);
                if (s.preferredQuote) setQuote(s.preferredQuote);
            })
            .catch(err => setMessage({ ok: false, text: processError(err) }));
    }, []);

    function pickPreset(p) {
        setPercent(String(Math.round(p.fraction * 100)));
        setThreshold(String(p.threshold));
        setProfile(p.key);
    }

    function onPercentChange(v) { setPercent(v); setProfile(null); }   // edição manual = Personalizado
    function onThresholdChange(v) { setThreshold(v); setProfile(null); }

    function save() {
        setBusy(true);
        updateSettings({
            withdrawSkimFraction: percent === '' ? null : (parseFloat(percent) / 100),
            withdrawSkimThreshold: threshold === '' ? null : parseFloat(threshold),
            withdrawProfile: profile
        })
            .then(() => setMessage({ ok: true, text: 'Realização de lucro salva.' }))
            .catch(err => setMessage({ ok: false, text: processError(err) }))
            .finally(() => setBusy(false));
    }

    function startBaseline() {
        setBusy(true);
        setSkimBaseline()
            .then(r => setMessage({ ok: true, text: `Ponto de partida fixado no lucro atual (${r.withdrawProfitHighWaterMark}). Saques futuros só realizam o lucro novo daqui pra frente.` }))
            .catch(err => setMessage({ ok: false, text: processError(err) }))
            .finally(() => setBusy(false));
    }

    const hasConfig = percent !== '' && parseFloat(percent) > 0;

    return (
        <div className='card card-body border-0 shadow mb-4'>
            <h5 className='mb-1'>🌱 Realização de Lucro</h5>
            <p className='small text-muted mb-3'>
                "Pague-se, mas deixe o bolo crescendo": a cada realização, sai só uma <strong>fração do lucro novo</strong> —
                o resto fica compondo e <strong>o principal nunca sai</strong>. Escolha um preset ou ajuste na mão.
            </p>

            {message && (
                <div className={`small mb-3 ${message.ok ? 'text-success' : 'text-danger'}`}>
                    {message.ok ? '✅ ' : '⚠ '}{message.text}
                </div>
            )}

            {/* Cartões de preset */}
            <div className='row g-2 mb-3'>
                {SKIM_PRESETS.map(p => {
                    const active = profile === p.key;
                    return (
                        <div className='col-md-4' key={p.key}>
                            <div
                                onClick={() => pickPreset(p)}
                                className={`border rounded p-3 h-100 ${active ? 'border-primary' : ''}`}
                                style={{ cursor: 'pointer', ...(active ? { borderWidth: 2, backgroundColor: '#eef4ff' } : {}) }}>
                                <div className='fw-bold mb-1'>{p.emoji} {p.name}{active ? ' ✓' : ''}</div>
                                <div className='small text-muted mb-2'>{p.desc}</div>
                                <div className='small'>
                                    Sai <strong>{Math.round(p.fraction * 100)}%</strong> do lucro · acima de <strong>{p.threshold} {quote}</strong>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Knobs */}
            <div className='row'>
                <div className='col-md-6 mb-2'>
                    <label className='form-label small'>Fração que sai (% do lucro novo)</label>
                    <input type='number' min='0' max='100' step='any' className='form-control form-control-sm'
                        value={percent} onChange={e => onPercentChange(e.target.value)}
                        placeholder='ex: 50' />
                </div>
                <div className='col-md-6 mb-2'>
                    <label className='form-label small'>Limiar — lucro novo mínimo (em {quote})</label>
                    <input type='number' min='0' step='any' className='form-control form-control-sm'
                        value={threshold} onChange={e => onThresholdChange(e.target.value)}
                        placeholder='ex: 50' />
                </div>
            </div>

            {/* Frase-resumo ao vivo */}
            {hasConfig && (
                <div className='alert alert-light border small py-2 mb-3'>
                    Sai <strong>{percent}%</strong> do lucro novo acima de <strong>{threshold || 0} {quote}</strong>;
                    o resto fica compondo. <strong>O principal nunca sai.</strong>
                    {profile ? '' : ' (configuração personalizada)'}
                </div>
            )}

            <p className='small text-muted mb-2'>
                Pra usar de verdade: crie um Modelo de Saque com valor <strong>"Realização de Lucro"</strong> e uma automação
                que dispara a realização. O saque passa pelas travas de segurança acima (whitelist, tetos, modo simulação).
            </p>

            <div className='d-flex gap-2 flex-wrap'>
                <button className='btn btn-primary btn-sm' disabled={busy} onClick={save}>
                    Salvar realização de lucro
                </button>
                <button className='btn btn-outline-secondary btn-sm' disabled={busy} onClick={startBaseline}
                    title='Fixa o lucro atual como ponto de partida — o 1º saque só realiza o lucro novo daqui pra frente, não o histórico'>
                    Começar a contar de agora
                </button>
            </div>
        </div>
    );
}

export default ProfitRealizationPanel;
